import type { ProjectStatus } from '@/shared/utils/format-status.js';

export type BusinessProcessResponse = {
    id: string;
    projectId: string;
    name: string;
    description: string | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
};

export type BusinessProcessProps = {
    id: string;
    projectId: string;
    name: string;
    description: string | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
};

export class BusinessProcessModel {
    private constructor(private readonly props: BusinessProcessProps) {}

    public static with(props: BusinessProcessProps): BusinessProcessModel {
        return new BusinessProcessModel(props);
    }

    public static withFromResponse(response: BusinessProcessResponse): BusinessProcessModel {
        return BusinessProcessModel.with({ ...response });
    }

    public static withFromResponseList(list: BusinessProcessResponse[]): BusinessProcessModel[] {
        return list.map(BusinessProcessModel.withFromResponse);
    }

    public get id(): string {
        return this.props.id;
    }

    public get projectId(): string {
        return this.props.projectId;
    }

    public get name(): string {
        return this.props.name;
    }

    public get description(): string | null {
        return this.props.description;
    }

    public get isActive(): boolean {
        return this.props.isActive;
    }

    public get createdAt(): string {
        return this.props.createdAt;
    }

    public get updatedAt(): string {
        return this.props.updatedAt;
    }

    public toObject(): BusinessProcessProps {
        return { ...this.props };
    }
}

export type CreateBusinessProcessParams = {
    name: string;
    description?: string | null;
};

export type UpdateBusinessProcessParams = {
    name?: string;
    description?: string | null;
};

export type ProjectUserResponse = {
    id: string;
    name: string;
    email: string;
    profile: string;
    isActive: boolean;
};

export type ProjectDocResponse = {
    id: string;
    projectId: string;
    category: string;
    fileName: string;
    fileUrl: string;
    fileSizeBytes: string | null;
    mimeType: string | null;
    uploadedAt: string;
    uploadedBy: string | null;
};

export type UploadDocumentParams = {
    category: string;
    file: File;
};

export type ContactInfo = {
    name: string | null;
    email: string | null;
    phone: string | null;
};

export type SlaOverrides = {
    slaHoursEstimateEf: number | null;
    slaHoursStartDev: number | null;
    slaHoursAnalyzeBug: number | null;
    slaHoursAnalyzeMinor: number | null;
    slaDaysValidateDoc: number | null;
    slaMinScoreAi: number | null;
    aiStrictnessLevel?: number | null;
    aiExtraSystemPrompt?: string | null;
};

export type ProjectResponse = {
    id: string;
    name: string;
    abbreviation: string;
    description: string | null;
    status: ProjectStatus;
    clientId: string;
    clientName: string;
    clientAbbreviation: string;
    clientPmName: string | null;
    clientPmEmail: string | null;
    clientPmPhone: string | null;
    agilePmName: string | null;
    agilePmEmail: string | null;
    agilePmPhone: string | null;
    slaHoursEstimateEf: number | null;
    slaHoursStartDev: number | null;
    slaHoursAnalyzeBug: number | null;
    slaHoursAnalyzeMinor: number | null;
    slaDaysValidateDoc: number | null;
    slaMinScoreAi: number | null;
    aiModel: string | null;
    aiStrictnessLevel: number | null;
    aiExtraSystemPrompt: string | null;
    jiraProjectKey: string | null;
    jiraBaseUrl: string | null;
    reportsUrl: string | null;
    businessProcesses: BusinessProcessResponse[];
    developmentsCount: number;
    createdAt: string;
};

export type ProjectProps = {
    id: string;
    name: string;
    abbreviation: string;
    description: string | null;
    status: ProjectStatus;
    clientId: string;
    clientName: string;
    clientAbbreviation: string;
    clientPmName: string | null;
    clientPmEmail: string | null;
    clientPmPhone: string | null;
    agilePmName: string | null;
    agilePmEmail: string | null;
    agilePmPhone: string | null;
    slaHoursEstimateEf: number | null;
    slaHoursStartDev: number | null;
    slaHoursAnalyzeBug: number | null;
    slaHoursAnalyzeMinor: number | null;
    slaDaysValidateDoc: number | null;
    slaMinScoreAi: number | null;
    aiModel: string | null;
    aiStrictnessLevel: number | null;
    aiExtraSystemPrompt: string | null;
    jiraProjectKey: string | null;
    jiraBaseUrl: string | null;
    reportsUrl: string | null;
    businessProcesses: BusinessProcessModel[];
    developmentsCount: number;
    createdAt: string;
};

export class ProjectModel {
    private constructor(private readonly props: ProjectProps) {}

    public static with(props: ProjectProps): ProjectModel {
        return new ProjectModel(props);
    }

    public static withFromResponse(response: ProjectResponse): ProjectModel {
        return ProjectModel.with({
            id: response.id,
            name: response.name,
            abbreviation: response.abbreviation,
            description: response.description ?? null,
            status: response.status,
            clientId: response.clientId,
            clientName: response.clientName,
            clientAbbreviation: response.clientAbbreviation,
            clientPmName: response.clientPmName ?? null,
            clientPmEmail: response.clientPmEmail ?? null,
            clientPmPhone: response.clientPmPhone ?? null,
            agilePmName: response.agilePmName ?? null,
            agilePmEmail: response.agilePmEmail ?? null,
            agilePmPhone: response.agilePmPhone ?? null,
            slaHoursEstimateEf: response.slaHoursEstimateEf ?? null,
            slaHoursStartDev: response.slaHoursStartDev ?? null,
            slaHoursAnalyzeBug: response.slaHoursAnalyzeBug ?? null,
            slaHoursAnalyzeMinor: response.slaHoursAnalyzeMinor ?? null,
            slaDaysValidateDoc: response.slaDaysValidateDoc ?? null,
            slaMinScoreAi: response.slaMinScoreAi ?? null,
            aiModel: response.aiModel ?? null,
            aiStrictnessLevel: response.aiStrictnessLevel ?? null,
            aiExtraSystemPrompt: response.aiExtraSystemPrompt ?? null,
            jiraProjectKey: response.jiraProjectKey ?? null,
            jiraBaseUrl: response.jiraBaseUrl ?? null,
            reportsUrl: response.reportsUrl ?? null,
            businessProcesses: BusinessProcessModel.withFromResponseList(response.businessProcesses ?? []),
            developmentsCount: response.developmentsCount,
            createdAt: response.createdAt
        });
    }

    public static withFromResponseList(list: ProjectResponse[]): ProjectModel[] {
        return list.map(ProjectModel.withFromResponse);
    }

    public get id(): string {
        return this.props.id;
    }

    public get name(): string {
        return this.props.name;
    }

    public get abbreviation(): string {
        return this.props.abbreviation;
    }

    public get status(): ProjectStatus {
        return this.props.status;
    }

    public get description(): string | null {
        return this.props.description;
    }

    public get clientId(): string {
        return this.props.clientId;
    }

    public get clientName(): string {
        return this.props.clientName;
    }

    public get clientAbbreviation(): string {
        return this.props.clientAbbreviation;
    }

    public get clientPmName(): string | null {
        return this.props.clientPmName;
    }

    public get clientPmEmail(): string | null {
        return this.props.clientPmEmail;
    }

    public get clientPmPhone(): string | null {
        return this.props.clientPmPhone;
    }

    public get agilePmName(): string | null {
        return this.props.agilePmName;
    }

    public get agilePmEmail(): string | null {
        return this.props.agilePmEmail;
    }

    public get agilePmPhone(): string | null {
        return this.props.agilePmPhone;
    }

    public get slaHoursEstimateEf(): number | null {
        return this.props.slaHoursEstimateEf;
    }

    public get slaHoursStartDev(): number | null {
        return this.props.slaHoursStartDev;
    }

    public get slaHoursAnalyzeBug(): number | null {
        return this.props.slaHoursAnalyzeBug;
    }

    public get slaHoursAnalyzeMinor(): number | null {
        return this.props.slaHoursAnalyzeMinor;
    }

    public get slaDaysValidateDoc(): number | null {
        return this.props.slaDaysValidateDoc;
    }

    public get slaMinScoreAi(): number | null {
        return this.props.slaMinScoreAi;
    }

    public get aiModel(): string | null {
        return this.props.aiModel;
    }

    public get aiStrictnessLevel(): number | null {
        return this.props.aiStrictnessLevel;
    }

    public get aiExtraSystemPrompt(): string | null {
        return this.props.aiExtraSystemPrompt;
    }

    public get jiraProjectKey(): string | null {
        return this.props.jiraProjectKey;
    }

    public get jiraBaseUrl(): string | null {
        return this.props.jiraBaseUrl;
    }

    public get reportsUrl(): string | null {
        return this.props.reportsUrl;
    }

    public get businessProcesses(): BusinessProcessModel[] {
        return this.props.businessProcesses;
    }

    public get developmentsCount(): number {
        return this.props.developmentsCount;
    }

    public get createdAt(): string {
        return this.props.createdAt;
    }

    public toObject(): ProjectProps {
        return { ...this.props };
    }
}

export type ProjectOptionResponse = {
    id: string;
    name: string;
    abbreviation: string;
    status: ProjectStatus;
    client: { id: string; name: string; abbreviation: string };
};

export type ProjectOptionProps = {
    id: string;
    name: string;
    abbreviation: string;
    status: ProjectStatus;
    clientId: string;
    clientName: string;
    clientAbbreviation: string;
};

export class ProjectOptionModel {
    private constructor(private readonly props: ProjectOptionProps) {}

    public static with(props: ProjectOptionProps): ProjectOptionModel {
        return new ProjectOptionModel(props);
    }

    public static withFromResponse(response: ProjectOptionResponse): ProjectOptionModel {
        return ProjectOptionModel.with({
            id: response.id,
            name: response.name,
            abbreviation: response.abbreviation,
            status: response.status,
            clientId: response.client.id,
            clientName: response.client.name,
            clientAbbreviation: response.client.abbreviation
        });
    }

    public static withFromResponseList(list: ProjectOptionResponse[]): ProjectOptionModel[] {
        return list.map(ProjectOptionModel.withFromResponse);
    }

    public get id(): string {
        return this.props.id;
    }

    public get name(): string {
        return this.props.name;
    }

    public get abbreviation(): string {
        return this.props.abbreviation;
    }

    public get status(): ProjectStatus {
        return this.props.status;
    }

    public get clientId(): string {
        return this.props.clientId;
    }

    public get clientName(): string {
        return this.props.clientName;
    }

    public get clientAbbreviation(): string {
        return this.props.clientAbbreviation;
    }

    public get client(): { id: string; name: string; abbreviation: string } {
        return { id: this.props.clientId, name: this.props.clientName, abbreviation: this.props.clientAbbreviation };
    }

    public toObject(): ProjectOptionProps {
        return { ...this.props };
    }
}

export type CreateProjectParams = {
    name: string;
    abbreviation: string;
    clientId: string;
    description?: string | null;
    clientPm?: ContactInfo;
    agilePm?: ContactInfo;
    slaOverrides?: SlaOverrides;
    aiModel?: string | null;
    jiraConfig?: { jiraProjectKey?: string | null; jiraBaseUrl?: string | null };
    reportsUrl?: string | null;
};

export type UpdateProjectParams = {
    name?: string;
    abbreviation?: string;
    description?: string | null;
    clientPm?: ContactInfo;
    agilePm?: ContactInfo;
    slaOverrides?: SlaOverrides;
    aiModel?: string | null;
    jiraConfig?: { jiraProjectKey?: string | null; jiraBaseUrl?: string | null };
    reportsUrl?: string | null;
};
